import { KeyboardArrowDownRounded } from '@mui/icons-material';
import { CircularProgress, FormControl, Grid, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAsyncAction, useStateRef } from 'app/services';
import { transportCanGenerationLoad } from 'app/store/transport';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';

const TransportTrackForm = forwardRef((props, ref) => {
  const theme = useTheme();
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);

  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(transportCanGenerationLoad);
  const { canGenerationItems } = useSelector((state) => state.transport);
  const { defaultValues, onSubmit } = props;
  const { register, control, formState, handleSubmit } = useForm({
    defaultValues: {
      parking_time: '',
      sensor_lost_cycles_count: '',
      number_m: '',
      ignore_lost_cycles_count: '',
      number_n: '',
      speed_mode: '',
      dut_guard_level: '',
      auto_staging_guard_speed: '',
      measurement_dut_level_number: '',
      can_generation_id: defaultValues ? defaultValues.can_generation_id : '',
    },
  });

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) =>
        onSubmit({
          parking_time: data.parking_time,
          sensor_lost_cycles_count: data.sensor_lost_cycles_count,
          number_m: data.number_m,
          ignore_lost_cycles_count: data.ignore_lost_cycles_count,
          number_n: data.number_n,
          speed_mode: data.speed_mode,
          dut_guard_level: data.dut_guard_level,
          auto_staging_guard_speed: data.auto_staging_guard_speed,
          measurement_dut_level_number: data.measurement_dut_level_number,
          can_generation: canGenerationItems.find((item) => item.id === data.can_generation_id)?.name,
        })
      )();
    },
  }));

  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.parking_time} margin='dense' variant='outlined' size='small' fullWidth>
            <InputLabel shrink>Установленное время парковки</InputLabel>
            <Controller
              name='parking_time'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='9999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,4}$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Установленное время парковки'
                      variant='outlined'
                      fullWidth
                      notched
                      placeholder='600'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.sensor_lost_cycles_count} margin='dense' variant='outlined' fullWidth>
            <InputLabel shrink>Кол-во циклов до потери датчика</InputLabel>
            <Controller
              name='sensor_lost_cycles_count'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,3}$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Кол-во циклов до потери датчика'
                      variant='outlined'
                      fullWidth
                      notched
                      placeholder='12'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.number_m} margin='dense' variant='outlined' fullWidth>
            <InputLabel shrink>Значение числа M</InputLabel>
            <Controller
              name='number_m'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,3}$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Значение числа M'
                      variant='outlined'
                      fullWidth
                      notched
                      placeholder='8'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.ignore_lost_cycles_count} margin='dense' variant='outlined' fullWidth>
            <InputLabel shrink>Кол-во циклов до потери в игноре</InputLabel>
            <Controller
              name='ignore_lost_cycles_count'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,3}$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Кол-во циклов до потери в игноре'
                      variant='outlined'
                      notched
                      fullWidth
                      placeholder='21'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.number_n} margin='dense' variant='outlined' fullWidth>
            <InputLabel shrink>Значение числа N</InputLabel>
            <Controller
              name='number_n'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,3}$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Значение числа N'
                      variant='outlined'
                      notched
                      fullWidth
                      placeholder='30'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.speed_mode} margin='dense' variant='outlined' fullWidth>
            <InputLabel shrink>Режим скорости (GPS=0, CAN=1,2)</InputLabel>
            <Controller
              name='speed_mode'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='9'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Режим скорости (GPS=0, CAN=1, 2)'
                      variant='outlined'
                      fullWidth
                      notched
                      placeholder='1'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.dut_guard_level} margin='dense' variant='outlined' fullWidth>
            <InputLabel shrink>Уровень ДУТ для авто охраны</InputLabel>
            <Controller
              name='dut_guard_level'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='9999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,4}$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Уровень ДУТ для авто охраны'
                      variant='outlined'
                      fullWidth
                      notched
                      placeholder='300'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.auto_staging_guard_speed} margin='dense' variant='outlined' fullWidth>
            <InputLabel shrink>Скорость автопостановки на охрану</InputLabel>
            <Controller
              name='auto_staging_guard_speed'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,3}$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Скорость автопостановки на охрану'
                      variant='outlined'
                      fullWidth
                      notched
                      placeholder='40'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.measurement_dut_level_number} margin='dense' variant='outlined' fullWidth>
            <InputLabel shrink>Число ед. измерения уровня ДУТ</InputLabel>
            <Controller
              name='measurement_dut_level_number'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='999'
                  maskChar=''
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{1,3}$/iu,
                    required: true,
                  })}
                  {...field}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='Число ед. измерения уровеня ДУТ'
                      variant='outlined'
                      fullWidth
                      notched
                      placeholder='20'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl error={!!formState.errors.can_generation_id} margin='dense' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Поколение CAN</InputLabel>
            {isFetchLoading && (
              <OutlinedInput
                disabled
                endAdornment={
                  <InputAdornment position='end'>
                    <CircularProgress size={16} />
                  </InputAdornment>
                }
              />
            )}
            {!isFetchLoading && (
              <Controller
                name='can_generation_id'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Поколение CAN'
                    IconComponent={KeyboardArrowDownRounded}
                    MenuProps={{
                      sx: {
                        '.MuiPaper-root': {
                          maxHeight: 200,
                          overflow: 'auto',
                        },
                        '.MuiPaper-root:before': {
                          display: 'none',
                        },
                      },
                    }}
                    {...register(field.name, {
                      pattern: /^[A-Za-zА-Яа-я0-9_\W\s]+$/iu,
                      required: true,
                    })}
                    notched={firstUpdate}
                    value={field.value}
                    onChange={field.onChange}>
                    {canGenerationItems.map((value, key) => (
                      <MenuItem key={key} disableRipple value={value.id}>
                        {value.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}></Controller>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </form>
  );
});

export default TransportTrackForm;
